import { useEffect, useState } from "react";
import { listInquiries } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { Reveal } from "@/components/Reveal";
import { RefreshCw, Mail, Phone } from "lucide-react";

const interestLabels = {
  bulls: "Bulls",
  "replacement-heifers": "Replacement Heifers",
  "embryo-transfer": "Embryo Transfer",
  "ai-program": "AI Program",
  seedstock: "Seedstock",
  general: "General",
};

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listInquiries();
      setItems(data);
    } catch (e) {
      console.error(e);
      setError("Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div
      data-testid={ADMIN.page}
      className="bg-bone text-ink min-h-screen pt-32 md:pt-40"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 pb-24">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
                — Admin · Inquiry Ledger
              </p>
              <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight text-ink">
                Correspondence
              </h1>
              <p className="mt-3 font-mono text-[11px] tracking-overline uppercase text-sage">
                {loading ? "Loading…" : `${items.length} entries`}
              </p>
            </div>
            <button
              type="button"
              onClick={load}
              data-testid={ADMIN.refresh}
              className="inline-flex items-center gap-3 px-5 py-3 border border-ink text-ink text-[12px] uppercase tracking-overline hover:bg-ink hover:text-bone transition-colors"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
        </Reveal>

        <div className="mt-10 border-t border-border_subtle">
          {error && (
            <div className="py-8 font-mono text-[11px] tracking-overline uppercase text-terracotta">
              {error}
            </div>
          )}
          {!error && !loading && items.length === 0 && (
            <div className="py-16 text-center font-serif text-2xl text-sage italic">
              No inquiries yet.
            </div>
          )}
          <ul className="divide-y divide-border_subtle">
            {items.map((it, idx) => (
              <li
                key={it.id}
                data-testid={ADMIN.row}
                className="grid grid-cols-12 gap-4 py-8"
              >
                <div className="col-span-12 md:col-span-2 font-mono text-[11px] tracking-overline uppercase text-sage">
                  <div>#{String(idx + 1).padStart(3, "0")}</div>
                  <div className="mt-1 normal-case tracking-normal text-[11px] text-ink/60">
                    {formatDate(it.created_at)}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-3">
                  <div className="font-serif text-2xl text-ink tracking-tight">
                    {it.name}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[13px] text-ink/70">
                    <Mail className="h-3.5 w-3.5" />
                    <a
                      href={`mailto:${it.email}`}
                      className="hover:text-terracotta transition-colors"
                    >
                      {it.email}
                    </a>
                  </div>
                  {it.phone && (
                    <div className="mt-0.5 flex items-center gap-2 text-[13px] text-ink/70">
                      <Phone className="h-3.5 w-3.5" />
                      <span>{it.phone}</span>
                    </div>
                  )}
                </div>
                <div className="col-span-12 md:col-span-2">
                  <span className="inline-block border border-ink/30 px-3 py-1 font-mono text-[10px] tracking-overline uppercase text-ink">
                    {interestLabels[it.interest] || it.interest}
                  </span>
                </div>
                <div className="col-span-12 md:col-span-5">
                  <p className="text-ink/80 leading-relaxed">{it.message}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function formatDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return v;
  }
}
